import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Search } from "lucide-react";
import api from "../../lib/api";
import { useTeams } from "../../lib/teams";
import DrillDiagramPreview from "./DrillDiagramPreview";

export default function ExercisePickerDialog({ open, onOpenChange, onPick }) {
  const { activeTeam } = useTeams();
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !activeTeam) return;
    (async () => {
      const { data } = await api.get(`/teams/${activeTeam.id}/exercises`);
      setExercises(data);
    })();
  }, [open, activeTeam]);

  const filtered = exercises.filter((e) =>
    `${e.name} ${e.category || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const pick = (e) => {
    onPick({
      name: e.name,
      duration: e.duration || 15,
      description: e.description || "",
      diagram: e.diagram || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scegli dalla libreria</DialogTitle>
          <DialogDescription className="sr-only">Seleziona un esercizio dalla libreria da inserire nella seduta.</DialogDescription>
        </DialogHeader>
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Cerca esercizio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="picker-search"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {filtered.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => pick(e)}
              className="text-left border border-slate-200 rounded-xl overflow-hidden hover:border-sky-400 hover:shadow transition"
              data-testid={`pick-exercise-${e.id}`}
            >
              <DrillDiagramPreview diagram={e.diagram} />
              <div className="p-3">
                <div className="font-medium text-sm truncate">{e.name}</div>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                  {e.category && (
                    <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-medium">
                      {e.category}
                    </span>
                  )}
                  <span>{e.duration || 15} min</span>
                </div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-10 text-center text-slate-400 text-sm">
              {exercises.length === 0
                ? "Nessun esercizio in libreria. Salva il primo dalla pagina Libreria."
                : "Nessun risultato."}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
